<template>
  <div class="c--shared_languages-list" :class="`languages-list-wrapper type-modal searchable}`">
    <div class="languages-list-container">
      <box-simple :shadowed="isShadowedBox" :rounded="isRoundedBox" class="w-100">
        <div class="languages-list-header px-20">
          <p class="title-sub">{{ title }}</p>
        </div>
        <div class="languages-list-search-container">
          <span class="icon search">
            <i class="airalo-icon-search" />
          </span>
          <input
            v-model="searchValue"
            :placeholder="searchInputPlaceholder"
            class="languages-list-search w-100"
            type="text"
            data-testid="language-search"
          />
          <span class="icon close-icon" v-if="searchValue.length > 0" @click.prevent="clearSearch">
            <i class="airalo-icon-close-round-filled" />
          </span>
        </div>
        <ul
          class="languages-list"
          :class="{
            'empty-list': !searchedLanguageList.length,
            'd-grid': searchedLanguageList.length > 0,
          }"
        >
          <li
            v-for="(language, index) in searchedLanguageList"
            :key="`popup-languages-list-${index}`"
            :class="{
              active: language.code === $i18n.locale,
              'is-browser-locale': browserLocale === language.code,
            }"
          >
            <div class="language-name" @click.prevent="clickItem">
              <a
                @click.prevent="changeLanguage(language)"
                class="d-flex justify-content-between align-items-center"
                :dir="$dir()"
                :data-testid="`${language.native}-language`"
              >
                <span :class="{ 'text-right': $dir() === 'rtl' }">
                  {{ language.native }}
                </span>
                <i v-if="language.code === $i18n.locale" class="airalo-icon-tick-round-filled" />
              </a>
            </div>
          </li>
          <li v-if="!searchedLanguageList.length" class="empty">
            <p>
              {{ emptyText.length > 0 ? emptyText : $t('global.no-language-available') }}
            </p>
          </li>
        </ul>
      </box-simple>
    </div>
  </div>
</template>

<script>
  import ClientSideCookies from 'js-cookie'
  import { dictionaryGet } from '~/plugins/vee-validator-dictionary'

  export default {
    components: {
      BoxSimple: () => import('~/components/card-boxes/box-type-simple.vue'),
      CountryNameInFlag: () => import('~/components/shared/country-name-in-flag.vue'),
    },
    props: {
      list: {
        type: Array,
        required: true,
      },

      title: {
        type: String,
        required: true,
      },
      description: {
        type: String,
        required: false,
        default: '',
      },
      emptyText: {
        type: String,
        required: false,
        default: '',
      },
      searchInputPlaceholder: {
        type: String,
        required: false,
        default: '',
      },
      isShadowedBox: {
        type: Boolean,
        required: false,
        default: false,
      },
      isRoundedBox: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data() {
      return {
        searchValue: '',
      }
    },
    computed: {
      searchedLanguageList() {
        if (this.searchValue) {
          return this.list.filter((language) => {
            return [language.native, language.english].join(',').toLowerCase().includes(this.searchValue.toLowerCase())
          })
        } else {
          return this.list
        }
      },
      browserLocale() {
        return this.$i18n.getBrowserLocale()
      },
    },
    methods: {
      clearSearch() {
        this.searchValue = ''
      },
      clickItem() {
        this.$emit('onClickItem')
      },

      async changeLanguage(language) {
        this.$utils.sendEvent('languageChangedEvent', { language_id: language.code, language_name: language.native })
        this.$utils.sendEvent('languageChangeV2', {
          previous_language: this.$i18n.locale,
          current_language: language.code,
        })

        ClientSideCookies.set('Airalo.locale', language.code, {
          expires: new Date(Date.now() + 60 * 60 * 60 * 24 * 365),
          httpOnly: false,
        })

        if (language.code === 'en') {
          ClientSideCookies.set('airalo_browser_language', language.code, {
            expires: new Date(Date.now() + 60 * 60 * 60 * 24 * 365),
            httpOnly: false,
          })
        }

        this.$axios.defaults.headers.common['Accept-Language'] = language.code
        await this.$i18n.setLocale(language.code)

        await this.$store.dispatch('multiCurrency/fetch_currencies', null)
        await this.$store.dispatch('coBranding/re_fetch_branding_config')

        this.$router.push(this.switchLocalePath(language.code))

        await this.$nextTick()
        await this.$validator.localize(language.code, dictionaryGet(this.$root.context.app))

        const languageMap = {
          'es-419': 'ES',
          'zh-CN': 'ZH-CN',
          'pt-BR': 'PT-BR',
        }

        if (window?.OneTrust) {
          await window.OneTrust.changeLanguage(languageMap[language.code] ? languageMap[language.code] : language.code)
        }

        if (this.$store.state.auth.loggedIn) {
          await this.$auth.fetchUser()
        }

        // @TODO: this must be removed when we be sure about removing fresh chat integraiton from GTM.
        if (window.fcWidget && window.initiateCall) {
          window.fcWidget.hide()

          setTimeout(() => {
            window.fcWidget.destroy()

            setTimeout(() => {
              window.initiateCall()
              window.fcWidget.hide()

              setTimeout(() => {
                window.fcWidget.show()
              }, 100)
            }, 100)
          }, 100)
        }
        // @TODO END
      },
    },
  }
</script>

<style lang="scss" scoped>
  .languages-list {
    overflow: auto;

    li {
      padding: 0;
      border-bottom: 1px solid #eeeeee;
    }

    &-container {
      display: flex;
      align-items: stretch;
    }

    &-search {
      border: none;
      border-radius: 0;
      background-color: transparent;
      padding: 0;
      min-height: 22px;

      color: #4a4a4a;
      font-size: 0.9375rem;
      letter-spacing: 0;
      line-height: 1;
      font-weight: 400;

      &::placeholder,
      &:-ms-input-placeholder,
      &::-ms-input-placeholder {
        color: #8a8a8a;
      }

      &-container {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;

        border: 1px solid #dddddd;
        border-radius: 7px;
        background-color: #fafafa;
        padding: 13px 0;

        margin: 20px;

        & + .languages-list {
          border-top: 1px solid #eeeeee;
        }

        .icon {
          position: relative;
          display: flex;

          &.search {
            margin-left: 20px;
            margin-right: 13px;

            [dir='rtl'] & {
              margin-right: 20px;
              margin-left: 13px;
            }
          }

          &.close-icon {
            margin-right: 17px;
            margin-left: 13px;
            cursor: pointer;

            [dir='rtl'] & {
              margin-right: 13px;
              margin-left: 17px;
            }
          }
        }
      }
    }

    &-wrapper {
      &.type-modal {
        .languages-list {
          padding: 0 0 0 20px;

          [dir='rtl'] & {
            padding: 0 20px 0 0;
          }

          &-container {
            height: 100%;
          }

          &.empty-list {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
          }

          li {
            padding: 0 20px 0 0;

            [dir='rtl'] & {
              padding: 0 0 0 20px;
            }

            &.empty {
              border-bottom: none;

              p {
                color: #8a8a8a;
                margin: 0;
                font-size: 0.9375rem;
                font-weight: 400;
                line-height: 1.4;
                letter-spacing: 0;
              }
            }

            &.is-browser-locale {
              grid-row-start: 1;
            }

            .language-name {
              width: 100%;

              a {
                font-size: 0.9375rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 1.3333;
                color: #4a4a4a;
                text-decoration: none;
                padding: 17px 0 16px;
                cursor: pointer;
              }

              span {
                display: inline-block;
                width: calc(100% - 30px);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                text-align: left;
              }
            }
          }
        }
      }
    }
  }
</style>
